/**
 * BenchmarkCharts — renders the chart subview above the markdown
 * viewer on /admin/llm-benchmarks. Sourced from the JSON sidecar
 * emitted by scripts/benchmark_llm_configs.py (CompareResult dump).
 *
 * Three charts per fixture:
 *   1. Cost per call — grouped bar, **log scale** (cost spans ~85×).
 *   2. Pass rate — grouped bar, linear (quality is statistically tied).
 *   3. Wins matrix — HTML/Tailwind heatmap table.
 *
 * Older runs (pre-2026-05-17) have no JSON sidecar; the parent treats
 * the 404 as "no charts available" and renders markdown-only.
 *
 * See: ai-docs/google-ads/p1-benchmark-charts-plan.md
 */
import { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type {
  BenchmarkAgentRun,
  BenchmarkCompareResult,
  BenchmarkConfig,
} from '@/services/llmBenchmarkService';

// 4-color palette pulled from the app theme tokens. If we ever add a
// 5th config we'll need a chart-only palette extension — flagged in
// the plan doc.
const CONFIG_COLORS: Record<string, string> = {
  'openrouter-haiku': 'var(--color-coral)',
  'openrouter-sonnet': 'var(--color-coral-dark)',
  'openrouter-gemini-pro': 'var(--color-teal-dark)',
  'openrouter-gemini-flash': 'var(--color-teal)',
};

function colorForConfig(configId: string, fallbackIdx: number): string {
  if (CONFIG_COLORS[configId]) return CONFIG_COLORS[configId];
  const fallback = [
    'var(--color-coral)',
    'var(--color-teal-dark)',
    'var(--color-coral-dark)',
    'var(--color-teal)',
  ];
  return fallback[fallbackIdx % fallback.length];
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

interface AgentMetrics {
  configId: string;
  agent: string;
  meanCost: number;
  meanPassRate: number;
  meanLatency: number;
  errorCount: number;
}

function passRateOf(run: BenchmarkAgentRun): number {
  if (!run.scorecard) return 1.0;
  // Backfilled records embed the exact pass rate (avoids inflating
  // entries[] to preserve 3-decimal precision). Native runs don't set
  // this and we fall through to entries-based computation.
  if (typeof run.scorecard.overall_pass_rate === 'number') {
    return run.scorecard.overall_pass_rate;
  }
  if (!run.scorecard.entries.length) return 1.0;
  const passed = run.scorecard.entries.filter((e) => e.passed).length;
  return passed / run.scorecard.entries.length;
}

function totalCostOf(run: BenchmarkAgentRun): number {
  return run.llm_calls.reduce((sum, m) => sum + (m.cost_usd ?? 0), 0);
}

function totalLatencyOf(run: BenchmarkAgentRun): number {
  return run.llm_calls.reduce((sum, m) => sum + (m.elapsed_seconds ?? 0), 0);
}

function aggregate(
  data: BenchmarkCompareResult,
  configId: string,
  agent: string,
): AgentMetrics {
  const runs = data.runs[configId]?.[agent] ?? [];
  if (!runs.length) {
    return { configId, agent, meanCost: 0, meanPassRate: 0, meanLatency: 0, errorCount: 0 };
  }
  const sum = runs.reduce(
    (acc, r) => ({
      cost: acc.cost + totalCostOf(r),
      pass: acc.pass + passRateOf(r),
      lat: acc.lat + totalLatencyOf(r),
      err: acc.err + (r.raised ? 1 : 0),
    }),
    { cost: 0, pass: 0, lat: 0, err: 0 },
  );
  return {
    configId,
    agent,
    meanCost: sum.cost / runs.length,
    meanPassRate: sum.pass / runs.length,
    meanLatency: sum.lat / runs.length,
    errorCount: sum.err,
  };
}

// Chart-friendly row: one per agent, with one column per config.
interface ChartRow {
  agent: string;
  [configId: string]: string | number;
}

function buildChartData(
  data: BenchmarkCompareResult,
  metric: 'meanCost' | 'meanPassRate',
): ChartRow[] {
  return data.agents.map((agent) => {
    const row: ChartRow = { agent };
    for (const cfg of data.configs) {
      const m = aggregate(data, cfg.config_id, agent);
      row[cfg.config_id] = metric === 'meanCost' ? m.meanCost : m.meanPassRate;
    }
    return row;
  });
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

interface BenchmarkChartsProps {
  data: BenchmarkCompareResult;
}

export function BenchmarkCharts({ data }: BenchmarkChartsProps) {
  const costData = useMemo(() => buildChartData(data, 'meanCost'), [data]);
  const passRateData = useMemo(() => buildChartData(data, 'meanPassRate'), [data]);
  const winsMatrix = useMemo(() => buildWinsMatrix(data), [data]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Cost per call</CardTitle>
          <CardDescription>
            Y-axis is logarithmic — cost spans up to ~85× across configs.
            Mean across seeds.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartWrapper>
            <BarChart data={costData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="agent" tick={{ fontSize: 12 }} />
              <YAxis
                scale="log"
                domain={['auto', 'auto']}
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) => `$${v.toFixed(4)}`}
                width={75}
              />
              <Tooltip
                formatter={(v: number) => [`$${v.toFixed(5)}`, 'cost']}
                contentStyle={{
                  fontSize: 12,
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  color: 'hsl(var(--foreground))',
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {data.configs.map((cfg, i) => (
                <Bar
                  key={cfg.config_id}
                  dataKey={cfg.config_id}
                  fill={colorForConfig(cfg.config_id, i)}
                />
              ))}
            </BarChart>
          </ChartWrapper>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pass rate</CardTitle>
          <CardDescription>
            Linear 0–1. Fraction of scorers each agent passed. Mean across seeds.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ChartWrapper>
            <BarChart data={passRateData} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="agent" tick={{ fontSize: 12 }} />
              <YAxis
                domain={[0, 1]}
                ticks={[0, 0.25, 0.5, 0.75, 1]}
                tick={{ fontSize: 11 }}
                tickFormatter={(v: number) => v.toFixed(2)}
                width={45}
              />
              <Tooltip
                formatter={(v: number) => [v.toFixed(3), 'pass rate']}
                contentStyle={{
                  fontSize: 12,
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: 8,
                  color: 'hsl(var(--foreground))',
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {data.configs.map((cfg, i) => (
                <Bar
                  key={cfg.config_id}
                  dataKey={cfg.config_id}
                  fill={colorForConfig(cfg.config_id, i)}
                />
              ))}
            </BarChart>
          </ChartWrapper>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pass-rate matrix</CardTitle>
          <CardDescription>
            One cell per (config × agent). Color intensity = pass rate; cell
            text = exact mean. Useful for spotting one-off wins (e.g., a single
            config holding 1.000 on a slot where everyone else dropped).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <WinsMatrix matrix={winsMatrix} />
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ChartWrapper({ children }: { children: React.ReactElement }) {
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
}

interface MatrixCell {
  passRate: number;
  cost: number;
}

interface WinsMatrixData {
  configs: BenchmarkConfig[];
  agents: string[];
  cells: Record<string, Record<string, MatrixCell>>;
  /** highest pass rate per agent — for "winner" highlighting */
  bestPerAgent: Record<string, number>;
}

function buildWinsMatrix(data: BenchmarkCompareResult): WinsMatrixData {
  const cells: WinsMatrixData['cells'] = {};
  const bestPerAgent: WinsMatrixData['bestPerAgent'] = {};

  for (const cfg of data.configs) {
    cells[cfg.config_id] = {};
    for (const agent of data.agents) {
      const m = aggregate(data, cfg.config_id, agent);
      cells[cfg.config_id][agent] = {
        passRate: m.meanPassRate,
        cost: m.meanCost,
      };
      const cur = bestPerAgent[agent] ?? -Infinity;
      if (m.meanPassRate > cur) bestPerAgent[agent] = m.meanPassRate;
    }
  }

  return { configs: data.configs, agents: data.agents, cells, bestPerAgent };
}

function WinsMatrix({ matrix }: { matrix: WinsMatrixData }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="border-b border-border p-2 text-left text-xs font-medium text-muted-foreground">
              Config \ Agent
            </th>
            {matrix.agents.map((agent) => (
              <th
                key={agent}
                className="border-b border-border p-2 text-center text-xs font-medium"
              >
                {agent}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.configs.map((cfg) => (
            <tr key={cfg.config_id}>
              <td className="border-b border-border/40 p-2 text-xs font-medium whitespace-nowrap">
                {cfg.config_id}
              </td>
              {matrix.agents.map((agent) => {
                const cell = matrix.cells[cfg.config_id]?.[agent];
                if (!cell) {
                  return (
                    <td
                      key={agent}
                      className="border-b border-border/40 p-2 text-center text-xs text-muted-foreground"
                    >
                      —
                    </td>
                  );
                }
                const isBest = matrix.bestPerAgent[agent] === cell.passRate;
                // Intensity = pass rate (0-1). Green channel for "good".
                const alpha = Math.max(0.05, cell.passRate);
                return (
                  <td
                    key={agent}
                    className="border-b border-border/40 p-2 text-center text-xs tabular-nums"
                    style={{
                      backgroundColor: `color-mix(in srgb, hsl(var(--status-success)) ${alpha * 50}%, transparent)`,
                      fontWeight: isBest ? 600 : 400,
                    }}
                    title={`pass=${cell.passRate.toFixed(3)} · cost=$${cell.cost.toFixed(5)}`}
                  >
                    {cell.passRate.toFixed(3)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

